import Decimal from "decimal.js";
import { resolveHelpers, inheritLaborTime } from "./helpers";
import TextField from "./CustomTextField";
import { Fragment, useState } from "react";
import { Box, Button, Icon, IconButton, Tooltip, Menu, MenuItem, Typography } from "@mui/material";
import RestartAlt from "@mui/icons-material/RestartAlt";
import CustomFieldTooltip from "./CustomFieldTooltip";
import { quantityExplanation, costDescriptionExplanation } from "./quantityExplanation";
import Add from "@mui/icons-material/Add";
import CustomMaterialLibraryDrawer from "./CustomMaterialLibraryDrawer";
import CustomLibraryDrawer, { LibraryCategory } from "./CustomLibraryDrawer";
import UiCard from "components/base/UiCard";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import {
  calculateCustom,
  rowQuantity,
  CostRow,
  Custom,
  money,
  truckingUnits,
  changeTruckingUnit,
  TruckingUnit,
  materialUnits,
} from "./calculator";
import CustomMaterialCalculationDrawer from "./CustomMaterialCalculationDrawer";
import { customNumberProps, customCompactFieldSx } from "./customStyles";

const unitOptions: Partial<Record<CostRow["category"], string[]>> = {
  labor: ["Hours", "Days", "Half Day", "Job", "Each"],
  trucking: truckingUnits,
  equipment: ["Hours", "Days", "Half Day", "Mileage", "Each"],
  material: materialUnits,
};

const calculationLabels: Partial<Record<CostRow["category"], string>> = {
  labor: "Calculate labor hours",
  material: "Calculate material quantity",
  equipment: "Calculate equipment hours",
  trucking: "Calculate trucking needs",
};

const titles = {
  labor: "Labor",
  material: "Materials",
  trucking: "Trucking",
  equipment: "Equipment",
  other: "Other costs",
};
// The same tracks and cell padding are used for every header and editable row.
const columns = {
  xs: "minmax(130px, 1.25fr) minmax(200px, 1fr) 216px 120px 90px 100px 40px",
  md: "minmax(130px, 1.25fr) minmax(200px, 1fr) 206px 120px 90px 100px 40px",
};
const fieldColumns = {
  name: 1,
  trucksNeeded: 2,
  sourcePlantName: 2,
  quantity: 3,
  unit: 4,
  rate: 5,
};

type Props = {
  category: CostRow["category"];
  rows: CostRow[];
  totals?: ReturnType<typeof calculateCustom>;
  readOnly: boolean;
  busy: boolean;
  settings: any;
  item: Custom;
  onItemChange: (patch: Partial<Custom>) => void;
  onCalculations?: (tab: number, rowIndex: number) => void;
  onChange: (index: number, patch: Partial<CostRow>) => void;
  onAdd: () => void;
  onDelete: (index: number) => void;
};
export default function CustomCostSection({
  category,
  rows,
  totals,
  readOnly,
  busy,
  settings,
  item,
  onItemChange,
  onCalculations,
  onChange,
  onAdd,
  onDelete,
}: Props) {
  let linkedRows: CostRow[] = item.rows;
  try {
    linkedRows = resolveHelpers(item, (r) => rowQuantity(item, r).quantity).rows;
  } catch {
    /* Visible validation is owned by the editor. */
  }
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const hasLibrary = ["labor", "equipment", "other", "trucking", "material"].includes(category);
  const units = unitOptions[category];
  const trucking = category === "trucking";
  const grid = {
    display: "grid",
    gridTemplateColumns: columns,
    alignItems: "center",
    minWidth: { xs: 920, md: 860 },
  };
  const fields = trucking
    ? (["name", "trucksNeeded", "quantity", "unit", "rate"] as const)
    : category === "material"
    ? (["name", "sourcePlantName", "quantity", "unit", "rate"] as const)
    : (["name", "quantity", "unit", "rate"] as const);
  const [helper, setHelper] = useState<number | null>(null);
  const matching = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.category === category);
  const total = matching.reduce(
    (sum, { index }) => sum + (totals?.rows[index].total.toNumber() ?? 0),
    0
  );
  return (
    <UiCard variant="section" sx={customCompactFieldSx}>
      <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
        <MenuItem
          disabled={busy || readOnly}
          onClick={() => {
            setAddAnchor(null);
            onAdd();
          }}
        >
          Add Custom
        </MenuItem>
        <MenuItem
          disabled={busy || readOnly}
          onClick={() => {
            setAddAnchor(null);
            setLibraryOpen(true);
          }}
        >
          Add from Library
        </MenuItem>
      </Menu>
      {libraryOpen && category === "material" && !readOnly && (
        <CustomMaterialLibraryDrawer
          item={item}
          settings={settings}
          disabled={busy}
          onClose={() => setLibraryOpen(false)}
          onAdd={(row) => onItemChange({ rows: [...item.rows, inheritLaborTime(item, row)] })}
        />
      )}
      {libraryOpen && hasLibrary && category !== "material" && !readOnly && (
        <CustomLibraryDrawer
          category={category as LibraryCategory}
          disabled={busy}
          onClose={() => setLibraryOpen(false)}
          onAdd={(rows) =>
            onItemChange({
              rows: [...item.rows, ...rows.map((row) => inheritLaborTime(item, row))],
            })
          }
        />
      )}

      <Box
        sx={{ overflowX: "auto" }}
        role="region"
        aria-label={`${titles[category]} cost table`}
        tabIndex={0}
      >
        <Box sx={{ ...grid, py: 0.25, minHeight: { xs: 44, md: 30 } }}>
          <Typography sx={{ gridColumn: "1 / -3", px: 0.75, fontSize: 13, fontWeight: 700 }}>
            {titles[category]}{" "}
            <Box
              component="span"
              sx={{ color: draftColors.textSecondary, fontSize: 12, fontWeight: 400 }}
            >
              ({matching.length})
            </Box>
          </Typography>
          <Typography
            data-custom-subtotal={category}
            sx={{
              gridColumn: "-3 / -2",
              px: 0.75,
              textAlign: "right",
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {totals ? money(total) : "—"}
          </Typography>
          <Box sx={{ gridColumn: "-2 / -1", display: "flex", justifyContent: "center" }}>
            {!readOnly && (
              <IconButton
                size="small"
                disabled={busy}
                aria-label={`Add ${titles[category]}`}
                aria-haspopup={hasLibrary ? "menu" : undefined}
                aria-expanded={hasLibrary ? !!addAnchor : undefined}
                sx={{ color: draftColors.green }}
                onClick={(event) => (hasLibrary ? setAddAnchor(event.currentTarget) : onAdd())}
              >
                <Add fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        {matching.length > 0 && (
          <Box role="table" aria-label={`${titles[category]} costs`}>
            <Box
              role="row"
              sx={{
                ...grid,
                bgcolor: draftColors.surfaceTableHeader,
                borderTop: `1px solid ${draftColors.border}`,
                borderBottom: `1px solid ${draftColors.border}`,
              }}
            >
              {(trucking
                ? [
                    "Cost description",
                    "Trucks Needed",
                    "Quantity",
                    "Unit",
                    "Rate ($)",
                    "Cost total",
                    "",
                  ]
                : category === "material"
                ? ["Cost description", "Plant", "Quantity", "Unit", "Rate ($)", "Cost total", ""]
                : ["Cost description", "Quantity", "Unit", "Rate ($)", "Cost total", ""]
              ).map((label, i) => (
                <Box
                  role="columnheader"
                  key={label}
                  sx={{
                    gridColumn: trucking || category === "material" || i === 0 ? i + 1 : i + 2,
                    px: 0.75,
                    py: 0.5,
                    textAlign: ["Quantity", "Trucks Needed", "Rate ($)", "Cost total"].includes(
                      label
                    )
                      ? "right"
                      : "left",
                    ...(label === "Quantity"
                      ? { pr: { xs: "110px", md: "94px" } }
                      : label === "Trucks Needed"
                      ? { pr: { xs: "48px", md: "32px" } }
                      : {}),
                    fontSize: 11,
                    fontWeight: 600,
                    color: draftColors.textLabel,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
            {matching.map(({ row, index }) => (
              <Fragment key={index}>
                <Box
                  role="row"
                  sx={{ ...grid, py: 0.25, borderBottom: `1px solid ${draftColors.borderSoft}` }}
                >
                  {fields.map((key) => (
                    <Box
                      role="cell"
                      key={key}
                      sx={{
                        gridColumn: fieldColumns[key],
                        px: 0.75,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                      }}
                    >
                      <CustomFieldTooltip
                        text={
                          key === "name"
                            ? costDescriptionExplanation(row)
                            : key === "quantity"
                            ? quantityExplanation(item, row)
                            : key === "sourcePlantName"
                            ? row.sourcePlantName ?? ""
                            : key === "trucksNeeded" && row.truckingUnit !== "Hours"
                            ? "For planning only; does not affect cost."
                            : ""
                        }
                      >
                        <TextField
                          preserveBlank={key === "trucksNeeded" && row.truckingUnit !== "Hours"}
                          overridden={
                            key === "quantity" &&
                            (row.calculation !== "manual" ||
                              ["duration", "travel", "duration_travel", "trucking"].includes(
                                row.quantitySource ?? ""
                              )) &&
                            row.quantityOverride != null
                          }
                          size="small"
                          fullWidth
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                            ...(key === "name" ? { maxWidth: "55ch" } : {}),
                          }}
                          type="text"
                          select={key === "unit" && !!units}
                          inputProps={{
                            "aria-label": `${key} for cost ${index + 1}`,
                            ...(["quantity", "trucksNeeded", "rate"].includes(key)
                              ? { ...customNumberProps, inputMode: "decimal" as const }
                              : {}),

                            style: {
                              textAlign: ["quantity", "trucksNeeded", "rate"].includes(key)
                                ? "right"
                                : "left",
                            },
                          }}
                          InputProps={{
                            readOnly:
                              key === "sourcePlantName" ||
                              (key === "trucksNeeded" && row.quantitySource === "trucking"),
                          }}
                          disabled={readOnly || busy}
                          value={
                            key === "quantity" &&
                            (row.calculation !== "manual" ||
                              ["duration", "travel", "duration_travel", "trucking"].includes(
                                row.quantitySource ?? ""
                              ))
                              ? row.quantityOverride ??
                                (row.quantitySource &&
                                !["manual", "material"].includes(row.quantitySource)
                                  ? linkedRows[index]?.quantity
                                  : undefined) ??
                                totals?.rows[index]?.quantity.toDecimalPlaces(6).toString() ??
                                ""
                              : key === "trucksNeeded" && row.quantitySource === "trucking"
                              ? linkedRows[index]?.trucksNeeded ?? ""
                              : row[key] ?? ""
                          }
                          onBlur={(e) => {
                            if (
                              key !== "quantity" ||
                              !["labor", "material"].includes(category) ||
                              readOnly ||
                              busy
                            )
                              return;
                            const linked =
                              row.calculation !== "manual" ||
                              ["duration", "travel", "duration_travel"].includes(
                                row.quantitySource ?? ""
                              );
                            if (linked && row.quantityOverride == null) return;
                            const value = e.target.value;
                            if (/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(value))
                              onChange(index, {
                                [linked ? "quantityOverride" : "quantity"]: new Decimal(
                                  value
                                ).toFixed(2, Decimal.ROUND_HALF_UP),
                              });
                          }}
                          onChange={(e) =>
                            onChange(
                              index,
                              trucking && key === "unit"
                                ? changeTruckingUnit(row, e.target.value as TruckingUnit)
                                : {
                                    ...(trucking && key === "trucksNeeded"
                                      ? { truckCountDefault: false }
                                      : {}),
                                    [key === "quantity" &&
                                    (row.calculation !== "manual" ||
                                      [
                                        "duration",
                                        "travel",
                                        "duration_travel",
                                        "trucking",
                                      ].includes(row.quantitySource ?? ""))
                                      ? "quantityOverride"
                                      : key]: e.target.value,
                                  }
                            )
                          }
                        >
                          {key === "unit" &&
                            units && [
                              ...(!units.includes(row.unit)
                                ? [
                                    <MenuItem key="saved-unit" value={row.unit} disabled>
                                      {row.unit}
                                    </MenuItem>,
                                  ]
                                : []),
                              ...units
                                .filter(
                                  (unit) =>
                                    !["density", "thickness_factor"].includes(row.calculation) ||
                                    ["Ton", "Pound"].includes(unit)
                                )
                                .map((unit) => (
                                  <MenuItem key={unit} value={unit}>
                                    {unit}
                                  </MenuItem>
                                )),
                            ]}
                        </TextField>
                      </CustomFieldTooltip>
                      {key === "quantity" && (
                        <Box sx={{ width: 60, flexShrink: 0 }}>
                          {(category === "material" ||
                            category === "trucking" ||
                            (["labor", "equipment"].includes(category) &&
                              row.unit.toLowerCase() === "hours")) && (
                            <Tooltip title={calculationLabels[category]}>
                              <span style={{ display: "flex" }}>
                                <Button
                                  variant="text"
                                  size="small"
                                  disabled={busy}
                                  aria-label={calculationLabels[category]}
                                  sx={{
                                    minWidth: 60,
                                    width: 60,
                                    backgroundColor: "transparent !important",
                                    color: `${draftColors.textSecondary} !important`,
                                    boxShadow: "none !important",
                                    "&:hover": {
                                      backgroundColor: "transparent !important",
                                      textDecoration: "underline",
                                    },
                                    "&.Mui-focusVisible": {
                                      outline: `2px solid ${draftColors.blue}`,
                                      outlineOffset: 2,
                                    },
                                    px: 0.5,
                                    py: 0,
                                    height: { xs: 40, md: 28 },
                                    minHeight: { xs: 40, md: 28 },
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "underline",
                                    flexShrink: 0,
                                    textTransform: "none",
                                  }}
                                  onClick={() =>
                                    category === "material"
                                      ? setHelper(index)
                                      : onCalculations?.(category === "trucking" ? 3 : 1, index)
                                  }
                                >
                                  Calculate
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {key === "quantity" && (
                        <Box sx={{ width: { xs: 40, md: 24 }, flexShrink: 0 }}>
                          {key === "quantity" &&
                            (row.calculation !== "manual" ||
                              ["duration", "travel", "duration_travel", "trucking"].includes(
                                row.quantitySource ?? ""
                              )) &&
                            row.quantityOverride != null &&
                            !readOnly && (
                              <Tooltip title="Restore calculation">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busy}
                                    aria-label={`Restore calculation for cost ${index + 1}`}
                                    onClick={() => onChange(index, { quantityOverride: null })}
                                  >
                                    <RestartAlt sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                      )}
                    </Box>
                  ))}
                  <Box
                    role="cell"
                    sx={{
                      px: 0.75,
                      textAlign: "right",
                      fontSize: 13,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {totals ? money(totals.rows[index].total) : "—"}
                  </Box>
                  <Box role="cell" sx={{ display: "flex", justifyContent: "center" }}>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        disabled={busy}
                        aria-label={`Delete ${row.name || category} cost`}
                        sx={{ color: draftColors.red }}
                        onClick={() => onDelete(index)}
                      >
                        <Icon fontSize="small">delete</Icon>
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {category === "material" &&
                  helper === index &&
                  (row.sourceType === "plant_product" && !readOnly ? (
                    <CustomMaterialLibraryDrawer
                      initialRow={row}
                      item={item}
                      settings={settings}
                      disabled={busy}
                      onClose={() => setHelper(null)}
                      onAdd={(updated) => onChange(index, updated)}
                    />
                  ) : (
                    <CustomMaterialCalculationDrawer
                      item={item}
                      row={row}
                      onApply={(patch) => onChange(index, patch)}
                      onClose={() => setHelper(null)}
                      readOnly={readOnly}
                      busy={busy}
                      settings={settings}
                    />
                  ))}
              </Fragment>
            ))}
          </Box>
        )}
      </Box>
    </UiCard>
  );
}
